import {useState} from 'react'
import {number as formatNumber} from '@github-ui/formatters'
import {Button, CounterLabel, Flash, FormControl, Radio, RadioGroup, Stack, Textarea} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {SyncIcon} from '@primer/octicons-react'
import styles from './BaseSharedDismissalDialog.module.css'
import {pluralizedTexts} from '../utils/pluralized-texts'

export interface BaseSharedDismissalDialogProps {
  count: number
  error: Error | null
  isPending?: boolean
  onSubmit: (arg0: {resolution: string; dismissalComment: string}) => void
  setOpen: (open: boolean) => void
  delegatedAlertDismissalEnabled: boolean
  codeSecurity: boolean
  dismissalOptions?: Array<{value: string; label: string; description: string}>
}

export function BaseSharedDismissalDialog({
  setOpen,
  count,
  dismissalOptions,
  error,
  isPending,
  delegatedAlertDismissalEnabled,
  codeSecurity,
  onSubmit,
}: BaseSharedDismissalDialogProps) {
  const [selectedCloseReason, setSelectedCloseReason] = useState('')
  const [dismissalComment, setDismissalComment] = useState('')

  const [commentValidationError, setCommentValidationError] = useState(false)

  const validDismissalComment = delegatedAlertDismissalEnabled ? !!dismissalComment.trim() : true
  const isValid = !!selectedCloseReason && validDismissalComment

  const handleRadioGroupChange = (value: string | null) => {
    setSelectedCloseReason(value ?? '')
  }

  const entity = codeSecurity ? 'alert' : 'finding'
  const {title, subtitle, submitButtonLabel, delegatedAlertDismissal, reasons} = pluralizedTexts(entity, count)

  const DEFAULT_DISMISSAL_OPTIONS = [
    {value: 'FALSE_POSITIVE', label: 'False positive', description: reasons.falsePositiveDescription},
    {value: 'USED_IN_TESTS', label: 'Used in tests', description: reasons.usedInTestsDescription},
    {value: 'WONT_FIX', label: "Won't fix", description: reasons.wontFixDescription},
  ]

  const dismissalOptionsValue = dismissalOptions?.length ? dismissalOptions : DEFAULT_DISMISSAL_OPTIONS

  const onSubmitDismissal = async () => {
    if (!isValid) {
      setCommentValidationError(true)
      return
    }

    setCommentValidationError(false)

    onSubmit({
      resolution: selectedCloseReason,
      dismissalComment,
    })
  }

  return (
    <Dialog
      width="large"
      height="auto"
      onClose={() => setOpen(false)}
      title={delegatedAlertDismissalEnabled ? delegatedAlertDismissal.title : title}
      subtitle={delegatedAlertDismissalEnabled ? delegatedAlertDismissal.subtitle : subtitle}
      renderFooter={() => (
        <Dialog.Footer>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={onSubmitDismissal}
            disabled={!isValid || isPending}
            leadingVisual={isPending ? SyncIcon : null}
          >
            <span>
              {delegatedAlertDismissalEnabled ? delegatedAlertDismissal.submitButtonLabel : submitButtonLabel}
            </span>
            {count > 1 && (
              <CounterLabel scheme="primary" className="ml-2">
                {formatNumber(count)}
              </CounterLabel>
            )}
          </Button>
        </Dialog.Footer>
      )}
    >
      <Stack gap="normal">
        {error && <Flash variant="danger">{error.message}</Flash>}
        <RadioGroup name="resolution" onChange={handleRadioGroupChange} disabled={isPending}>
          <RadioGroup.Label visuallyHidden>Dismissal reason</RadioGroup.Label>
          {dismissalOptionsValue.map(option => (
            <FormControl key={option.value}>
              <Radio value={option.value} />
              <FormControl.Label>{option.label}</FormControl.Label>
              <FormControl.Caption>{option.description}</FormControl.Caption>
            </FormControl>
          ))}
        </RadioGroup>
        <div>
          <FormControl disabled={isPending}>
            <FormControl.Label required={delegatedAlertDismissalEnabled}>Comment</FormControl.Label>
            <Textarea
              placeholder="Add a comment"
              rows={5}
              maxLength={280}
              name="dismissal_comment"
              onChange={e => setDismissalComment(e.target.value)}
              className={styles.dismissalCommentTextarea}
            />
            {commentValidationError && (
              <FormControl.Validation variant="error">This field is required</FormControl.Validation>
            )}
          </FormControl>
        </div>
      </Stack>
    </Dialog>
  )
}
